/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from 'recharts';
import { Box, Typography, Grid } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import type { DriverBehavior } from 'types/driver.behavior.types';
import { truncateTo2dp } from 'utils/helpers/general';
import type { AssetFuelConsumption } from 'store/planningAid.slice';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  underspeed: DriverBehavior[];
  overspeed: DriverBehavior[];
  data: AssetFuelConsumption[];
}

export const OperatorBehavior = ({ enableDarkTheme, data }: Props) => {
  const { t } = useTranslation();
  const [selectedAsset, setSelectedAsset] = useState<string>('');
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const COLORS = {
    normal: palette.primary,
    highlight: '#EB5757',
  };
  const fontColor = palette.textPrimary;

  const formattedData: AssetFuelConsumption[] = data
    .map((item) => {
      const total = Object.values(item).reduce<number>(
        (sum, value) => (typeof value === 'number' ? sum + value : sum),
        0
      );

      return { ...item, total };
    })
    .sort((a, b) => b.total - a.total);

  const selectedAssetData = useMemo(() => {
    return (
      formattedData.find((item) => item.device === selectedAsset) ??
      formattedData[0]
    );
  }, [formattedData, selectedAsset]);

  const chartData = useMemo(() => {
    if (!selectedAssetData) return [];

    const dataKeys = Object.keys(selectedAssetData).filter(
      (k) => k !== 'device' && k !== 'total'
    );

    return dataKeys
      .map((key) => ({
        attribute: key,
        value: Number(selectedAssetData[key] as number),
      }))
      .sort((a, b) => b.value - a.value);
  }, [selectedAssetData]);

  return (
    <Box
      sx={{
        border: '1px solid #2F445E',
        borderRadius: '4px',
        miaxHeight: '300px',
        overflow: 'scroll',
        color: fontColor,
        padding: '20px',
        marginTop: '20px',
      }}
    >
      <Grid container spacing={2}>
        <Grid item lg={6} sm={12}>
          <Box
            sx={{
              minHeight: '300px',
              color: palette.textPrimary,
              backgroundColor: palette.paperBackground,
              padding: '10px',
              border: '1px solid #2F445E',
              borderRadius: '4px',
            }}
          >
            <Box
              sx={{
                height: '300px',
                maxHeight: '300px',
              }}
            >
              <div style={{ width: '100%', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={formattedData}
                    margin={{ top: 30, right: 90, left: 10, bottom: -10 }}
                  >
                    <CartesianGrid
                      stroke={palette.cartesianGrid}
                      strokeDasharray="2 2"
                      vertical={false}
                    />
                    <XAxis dataKey="device" />
                    <YAxis
                      label={{
                        value: 'Fuel Used',
                        angle: -90,
                        position: 'insideLeft',
                      }}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) =>
                        Math.round(Number(value)).toString()
                      }
                      domain={[0, 'auto']}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{
                        fill: enableDarkTheme ? '#1B2B4A' : '#CCCCCC',
                      }}
                      content={<CustomTooltip />}
                    />
                    <Bar
                      dataKey="total"
                      radius={[4, 4, 0, 0]}
                      fill={palette.primary}
                      onClick={(data) => {
                        setSelectedAsset(data.device);
                      }}
                      cursor="pointer"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} sm={12}>
          <Box
            sx={{
              maxHeight: '300px',
              color: palette.textPrimary,
              backgroundColor: palette.paperBackground,
              padding: '10px',
              border: '1px solid #2F445E',
              borderRadius: '4px',
            }}
          >
            {selectedAssetData && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: fontColor,
                }}
              >
                {t('displaying_distribution_for')} {selectedAssetData.device}
              </Typography>
            )}
            <Box
              sx={{
                height: '300px',
                maxHeight: '300px',
              }}
            >
              {chartData.length === 0 ? (
                <Typography
                  sx={{
                    color: fontColor,
                    fontSize: '12px',
                    marginTop: '10px',
                  }}
                >
                  {t('no_events_for_selected_asset')}
                </Typography>
              ) : (
                <div style={{ width: '100%', height: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 30, right: 30, left: 10, bottom: 30 }}
                      barCategoryGap="20%"
                    >
                      <CartesianGrid
                        stroke={enableDarkTheme ? '#1B2B4A' : '#CCCCCC'}
                        strokeDasharray="2 2"
                      />
                      <XAxis dataKey="attribute" tick={{ fontSize: 11 }} />
                      <YAxis
                        tickFormatter={(value) =>
                          Math.round(Number(value)).toString()
                        }
                        domain={[0, 'auto']}
                        allowDecimals={false}
                        tick={{ fontSize: 11 }}
                        label={{
                          angle: -90,
                          position: 'inside',
                          fill: '#9CA3AF',
                          dy: 10,
                          dx: -12,
                          fontSize: '10px',
                        }}
                      />
                      <Tooltip
                        cursor={{
                          fill: enableDarkTheme ? '#1B2B4A' : '#CCCCCC',
                        }}
                        content={<CustomTooltip />}
                      />

                      <Bar
                        dataKey="value"
                        fill={COLORS.normal}
                        radius={[4, 4, 0, 0]}
                      >
                        {chartData.map((entry, index) => {
                          const color = COLORS.normal;
                          return <Cell key={`cell-${index}`} fill={color} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: any;
}) => {
  if (active && payload && payload.length > 0) {
    const value =
      truncateTo2dp(payload[0].payload.value ?? payload[0].payload.total) ??
      'N/A';
    return (
      <div
        style={{
          backgroundColor: 'rgba(217, 229, 247, 0.8) ',
          border: '1px solid #ccc',
          color: '#000',
          padding: '3px 5px',
          borderRadius: '4px',
        }}
      >
        <p>{`Value: ${value}`}</p>
      </div>
    );
  }
  return null;
};
